import { useEffect, useState } from 'react'
import type { BacklogMember, ProjectDetail } from '@ai-meeting/shared'
import { ApiError } from '@/services/api'
import { fetchBacklogMembers } from '@/services/backlog'
import { fetchProject } from '@/services/projects'

/**
 * What the screen is showing. `missing` is the 404 — the grant works and there
 * is simply no such project, which reads nothing like "connect Backlog".
 */
export type ProjectDetailState = 'loading' | 'ready' | 'error' | 'connect' | 'reauth' | 'missing'

export interface UseProjectDetailResult {
  state: ProjectDetailState
  errorMessage: string | null
  project: ProjectDetail | null
  members: BacklogMember[]
  /** True once the member request settled, however it settled. */
  membersLoaded: boolean
  /** The roster could not be read; the rest of the screen is unaffected. */
  membersFailed: boolean
  /**
   * Refetch project facts only (meetingCount, lastMeetingAt, …). The roster
   * stays as last loaded — mutations on the Meetings tab do not change who
   * is on the project.
   */
  reload: () => void
}

/**
 * Loads one project and its roster.
 *
 * Both go out together rather than the roster waiting for its tab: the tab
 * carries a count, and a badge that appears a second after the tabs do reads
 * as the page still loading. They are separate requests so a space that
 * refuses the roster still leaves the overview standing.
 *
 * @param projectKey Backlog's short code, taken from the route.
 */
export function useProjectDetail(projectKey: string): UseProjectDetailResult {
  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [state, setState] = useState<ProjectDetailState>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [members, setMembers] = useState<BacklogMember[]>([])
  const [membersLoaded, setMembersLoaded] = useState(false)
  const [membersFailed, setMembersFailed] = useState(false)

  useEffect(() => {
    let active = true
    setState('loading')
    setMembersLoaded(false)
    setMembersFailed(false)

    void fetchProject(projectKey)
      .then((response) => {
        if (!active) return
        setProject(response.project)
        if (response.status === 'CONNECTION_REQUIRED') return setState('connect')
        if (response.status === 'REAUTH_REQUIRED') return setState('reauth')
        setState('ready')
      })
      .catch((error: unknown) => {
        if (!active) return
        if (error instanceof ApiError && error.status === 404) return setState('missing')
        setErrorMessage(error instanceof Error ? error.message : null)
        setState('error')
      })

    // Its own rejection is caught here rather than left to travel, so a space
    // that refuses the roster cannot take the overview down with it.
    void fetchBacklogMembers(projectKey)
      .then((page) => {
        if (!active) return
        setMembers(page.members ?? [])
        setMembersFailed(false)
        setMembersLoaded(true)
      })
      .catch(() => {
        if (!active) return
        setMembers([])
        setMembersFailed(true)
        setMembersLoaded(true)
      })

    return () => {
      active = false
    }
  }, [projectKey])

  function reload(): void {
    void fetchProject(projectKey)
      .then((response) => {
        setProject(response.project)
        if (response.status === 'CONNECTION_REQUIRED') return setState('connect')
        if (response.status === 'REAUTH_REQUIRED') return setState('reauth')
        setState('ready')
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return setState('missing')
        setErrorMessage(error instanceof Error ? error.message : null)
        setState('error')
      })
  }

  return { state, errorMessage, project, members, membersLoaded, membersFailed, reload }
}
